/** Library labels are discovery hints, never acoustic proof or instructions. */
export function loopMetadata(asset = {}) {
  const label = [asset.folder, asset.drumName, asset.category, asset.filename, asset.name].filter(Boolean).join(' ');
  const explicit = asset.assetType === 'loop' || asset.fingerprint?.assetType === 'loop';
  const named = /(?:^|[\s/\\_-])loops?(?:$|[\s/\\_.-])/i.test(label);
  const oneShot = /one[ _-]?shots?/i.test(label);
  const analysis = asset.sampleAnalysis || asset.fingerprint?.sampleAnalysis;
  const bpmMatch = /(?:(?:^|[^A-Za-z0-9])(\d{2,3}(?:\.\d+)?)[ _-]*bpm(?=$|[^A-Za-z0-9])|(?:^|[^A-Za-z0-9])bpm[ _-]*(\d{2,3}(?:\.\d+)?))/i.exec(label);
  const taggedBpm = Number(bpmMatch?.[1] || bpmMatch?.[2]);
  const validBpm = value => Number.isFinite(Number(value)) && Number(value) >= 40 && Number(value) <= 300;
  const recording = asset.fingerprint?.audioRole === 'recording';
  const tempoLabelled = validBpm(taggedBpm) && asset.assetType !== 'oneshot'
    && !/\b(?:808|kick|snare|clap|hi[ _-]?hat|hihat|perc)\b/i.test(label);
  const declaredBpm = validBpm(asset.bpm) ? Number(asset.bpm) : validBpm(taggedBpm) ? taggedBpm : null;
  const bpm = declaredBpm || (validBpm(analysis?.tempo?.bpm) ? analysis.tempo.bpm : null);
  const keyMatch = /(?:^|[^A-Za-z0-9])([A-G](?:#|b)?)[ _-]*(major|minor|maj|min|m)(?=$|[^A-Za-z0-9])/i.exec(label);
  const musicalKey = asset.musicalKey || (keyMatch ? `${keyMatch[1][0].toUpperCase()}${keyMatch[1].slice(1).toLowerCase()} ${/^m(?:in(?:or)?)?$/.test(keyMatch[2].toLowerCase()) ? 'minor' : 'major'}` : null);
  const duration = Number(asset.duration ?? asset.durationSeconds ?? (asset.durationMs || asset.fingerprint?.durationMs) / 1000) || Number(analysis?.duration) || null;
  const barsMatch = /\b(\d{1,2})[ _-]*bars?\b/i.exec(label);
  return { isLoop: explicit || recording || ((named || tempoLabelled) && !oneShot), bpm, musicalKey, duration,
    bars: Number(asset.bars || barsMatch?.[1]) || null,
    tempoSource: asset.loopEvidence?.tempoSource || (declaredBpm ? (asset.bpm ? 'catalog' : 'filename') : bpm ? 'estimated-audio' : 'unknown'),
    tempoConfidence: declaredBpm ? null : analysis?.tempo?.confidence ?? null,
    keySource: musicalKey ? (asset.musicalKey ? 'catalog' : 'filename') : 'unknown',
    analysis: analysis || null };
}

export function isLoopProductionRequest(prompt, state = {}) {
  const text = String(prompt || '');
  if (/\b(?:new|another|different)\b.{0,30}\b(?:beat|song|instrumental)\b/i.test(text)
    && !/\b(?:loops?|recording|wav)\b/i.test(text)) return false;
  if (/\b(?:mute|unmute|solo|unsolo|delete|remove|rename)\b.{0,40}\bloop\b/i.test(text)) return false;
  if (/\b(?:compose|write|create|make)\b.{0,40}\b(?:original|from scratch)\b/i.test(text)
    && !/\b(?:browser|kits?|library|existing)\b/i.test(text)) return false;
  return /\b(?:use|find|grab|pick|choose|browse|from|around|over|with|my|this|that)\b.{0,90}\bloops?\b|\bloops?\b.{0,90}\b(?:browser|kits?|library|drums?|layers?|808|flip)\b/i.test(text)
    || /\b(?:use|place|add|import|arrange)\b.{0,80}\b(?:wav|recording|recorded audio)\b/i.test(text)
    || ((state?.tracks || []).some(track => track.loopSource) && /\b(?:drums?|layers?|melod|bass|808|beat|percussion)\b/i.test(text));
}

/** Kept through website save/reload as well as the plugin's saved snapshot. */
export function persistedLoopFields(track = {}) {
  if (!track.loopSource?.assetId) return {};
  return { role: 'loop', sourceType: track.sourceType || track.loopSource.sourceType,
    assetId: track.assetId || track.loopSource.assetId, assetVersion: track.assetVersion || 1,
    ...(track.audioWaveform ? { audioWaveform: track.audioWaveform } : {}),
    loopSource: { ...track.loopSource } };
}
